// The person's achievements board (`GET /v1/me/achievements`, spec web/achievements) and its three
// writes, shared by the Achievements tab, the gift menu and the unlock card. A write shows on the
// board at once, and on every read that lands while it is on its way, so the switches, the count
// and the list follow it. The switches' saves reach the server one at a time and in order. Once
// the last write on its way is done, the board is read again, which says what they all kept
// (decision 17).
import { skipToken, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import {
  getGetMyAchievementsQueryKey,
  getMyAchievements,
  useGetMyAchievements,
  useMarkAchievementsSeen,
  useSetAchievementsHidden,
  useSetAchievementsShared,
} from "../../api/generated/endpoints/achievements/achievements";
import type { AchievementBoard, HiddenUpdate, SharingUpdate } from "../../api/generated/model";
import { seenAwardsAwardIdsMax } from "../../api/generated/zod";

const KEY = getGetMyAchievementsQueryKey();

/** A switch: Hide, or Share in one team. */
type Switch = "hidden" | `sharing ${string}`;

/** A turn of a switch: its place among the tab's turns, the value it sets and the one shown before. */
interface Turn {
  key: Switch;
  seq: number;
  value: boolean;
  before: boolean;
}

/** The board's writes on their way, page or no page. */
const writes = {
  /** How many: marks and switch saves. */
  count: 0,
  /** Each award a mark names, by how many marks name it; a refused mark's leave at once. */
  seen: new Map<string, number>(),
  /** Each switch's newest turn; a refused newest turn leaves at once. */
  turns: new Map<Switch, Turn>(),
};

let turns = 0;

const shownOf = (board: AchievementBoard, key: Switch): boolean =>
  key === "hidden" ? board.hidden : board.sharing.some((team) => key === `sharing ${team.team_id}` && team.shared);

function withSwitch(board: AchievementBoard, key: Switch, value: boolean): AchievementBoard {
  return key === "hidden"
    ? { ...board, hidden: value }
    : { ...board, sharing: board.sharing.map((team) => (key === `sharing ${team.team_id}` ? { ...team, shared: value } : team)) };
}

/** The board as the writes on their way leave it: a read that left before one landed would undo it. */
function withWrites(board: AchievementBoard): AchievementBoard {
  if (writes.count === 0) return board;
  let shown = { ...board, unseen: board.unseen.filter((each) => !writes.seen.has(each.award_id)) };
  for (const turn of writes.turns.values()) shown = withSwitch(shown, turn.key, turn.value);
  return shown;
}

/** Every read of the board takes the writes on their way into account as it lands. */
export const useBoard = () =>
  useGetMyAchievements({ query: { queryFn: ({ signal }) => getMyAchievements({ signal }).then(withWrites) } });

/**
 * A write's change to the board in the cache. It keeps the time of the board's last read from the
 * server, so a stale board stays stale and the change never passes for a read (decision 2).
 */
function change(queryClient: QueryClient, next: (board: AchievementBoard) => AchievementBoard): void {
  const updatedAt = queryClient.getQueryState(KEY)?.dataUpdatedAt;
  if (updatedAt === undefined) return;
  queryClient.setQueryData<AchievementBoard>(KEY, (board) => board && next(board), { updatedAt });
}

/** A write sets off: the board shows it at once, a read on its way meanwhile being cancelled. */
async function setOff(queryClient: QueryClient, note: () => void): Promise<void> {
  writes.count += 1;
  note();
  await queryClient.cancelQueries({ queryKey: KEY });
  change(queryClient, withWrites);
}

/** A write is done: after the last, the board is read again, any read already on its way cancelled. */
function done(queryClient: QueryClient): void {
  writes.count -= 1;
  if (writes.count > 0) return;
  writes.seen.clear();
  writes.turns.clear();
  void queryClient.invalidateQueries({ queryKey: KEY });
}

/** The latest marks' refusal, wherever they were made: kept in the query cache, never fetched. */
const REFUSAL = ["achievements-seen-refusal"] as const;

/**
 * Mark unlocks seen: they leave the count and the list at once. One mark names at most the
 * contract's `seenAwardsAwardIdsMax` awards, so a longer list goes as several, each on its own. A
 * refused one's unlocks come back with the board's next read, and once the list's marks are done,
 * `error` is their refusal, or null, wherever the marking was (the tab, the gift menu or a card),
 * so the tab and the menu say so (decision 16).
 */
export function useMarkSeen() {
  const queryClient = useQueryClient();
  const error = useQuery<unknown>({ queryKey: REFUSAL, queryFn: skipToken }).data ?? null;
  const { mutateAsync } = useMarkAchievementsSeen({
    mutation: {
      onMutate: ({ data }) =>
        setOff(queryClient, () => {
          for (const id of data.award_ids) writes.seen.set(id, (writes.seen.get(id) ?? 0) + 1);
        }),
      onError: (_error, { data }) => {
        for (const id of data.award_ids) {
          const left = (writes.seen.get(id) ?? 1) - 1;
          if (left > 0) writes.seen.set(id, left);
          else writes.seen.delete(id);
        }
      },
      onSettled: () => done(queryClient),
    },
  });
  const mark = useCallback(
    (awardIds: readonly string[]) => {
      const marks: Promise<unknown>[] = [];
      for (let at = 0; at < awardIds.length; at += seenAwardsAwardIdsMax) {
        marks.push(mutateAsync({ data: { award_ids: awardIds.slice(at, at + seenAwardsAwardIdsMax) } }));
      }
      if (marks.length === 0) return;
      void Promise.allSettled(marks).then((ends) => {
        const refused = ends.find((end): end is PromiseRejectedResult => end.status === "rejected");
        queryClient.setQueryData<unknown>(REFUSAL, refused === undefined ? null : (refused.reason as unknown));
      });
    },
    [mutateAsync, queryClient],
  );
  return { mark, error };
}

/** The switches' saves, Hide and each team's Share, one at a time and in order (TanStack's mutation scope). */
const SWITCH_SAVES = { id: "achievements-switches" };

/**
 * A switch's save: shown at once, and sent after the saves before it, so the server keeps the
 * newest turn. A refused turn that is still its switch's newest puts back what that switch showed
 * before it, and the board read once the last write is done says what every save kept: an older
 * turn's answer, or a read that left before it, cannot leave a switch showing what the server did
 * not keep (decision 17, as the account's theme does: themes/account.ts).
 */
function switchSave<V>(queryClient: QueryClient, turnOf: (variables: V) => [Switch, boolean]) {
  return {
    scope: SWITCH_SAVES,
    onMutate: async (variables: V): Promise<Turn> => {
      const [key, value] = turnOf(variables);
      const board = queryClient.getQueryData<AchievementBoard>(KEY);
      turns += 1;
      const turn: Turn = { key, seq: turns, value, before: board === undefined ? !value : shownOf(board, key) };
      await setOff(queryClient, () => writes.turns.set(key, turn));
      return turn;
    },
    onError: (_error: unknown, _variables: V, turn: Turn | undefined) => {
      if (turn === undefined || writes.turns.get(turn.key)?.seq !== turn.seq) return;
      writes.turns.delete(turn.key);
      change(queryClient, (board) => withSwitch(withWrites(board), turn.key, turn.before));
    },
    onSettled: () => done(queryClient),
  };
}

/** Hide achievements, or show them again (decision 1.3). */
export function useHiddenSwitch() {
  const queryClient = useQueryClient();
  return useSetAchievementsHidden<unknown, Turn>({
    mutation: switchSave(queryClient, ({ data }: { data: HiddenUpdate }) => ["hidden", data.hidden]),
  });
}

/** Share in one team what was earned in it, or stop (decision 1.2). */
export function useSharingSwitch() {
  const queryClient = useQueryClient();
  return useSetAchievementsShared<unknown, Turn>({
    mutation: switchSave(queryClient, ({ teamId, data }: { teamId: string; data: SharingUpdate }) => [
      `sharing ${teamId}`,
      data.shared,
    ]),
  });
}
